import type { ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { MyBookingsLink, TopBar } from './TopBar';
import { PhotoHeader } from './PhotoHeader';
import { BusinessHeader } from './BusinessHeader';
import { heroHasDetails } from '../helpers';
import type { ProfileDisplayOptions, PublicOrganization } from '../types';

interface BookingPageFrameProps {
  organization: PublicOrganization;
  display: ProfileDisplayOptions;
  // Bottom padding of the column: the booking steps leave room for the fixed
  // mobile bar.
  bottomPadding: string;
  contentRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}

// The part of the booking page that stays put while the visitor moves from
// the profile (step 0) into the booking steps: top bar, photos, business
// header and footer. Only `children` changes, so picking a service swaps the
// content below the header instead of loading a new page.
export function BookingPageFrame({ organization, display, bottomPadding, contentRef, children }: BookingPageFrameProps) {
  const { t } = useTranslation('bookingPage');
  const page = organization.booking_page;
  const photosShown = display.photos && page.layout !== 'none' && page.photos.length > 0;
  // Without the top bar (embed header=0) the hero is the only place the
  // business name appears, so it always stays.
  const bandShown = photosShown || !display.header || heroHasDetails(organization);

  return (
    <div className="flex min-h-screen flex-col bg-bg-public text-ink">
      {display.header && <TopBar organization={organization} embedded={display.embedded} />}

      <main className={cn('mx-auto w-full max-w-[1440px] px-4 sm:px-8 lg:px-20', display.embedded ? 'pt-4 sm:pt-6' : 'sm:pt-8', bottomPadding)}>
        {display.header && <MyBookingsLink embedded={display.embedded} className="mt-4 flex w-full sm:hidden" />}

        {bandShown ? (
          <div
            data-testid="step-band"
            data-variant={photosShown ? 'photos' : 'logo'}
            className={cn(photosShown && display.header && 'mt-4 sm:mt-0')}
          >
            {photosShown && <PhotoHeader layout={page.layout} photos={page.photos} orgName={organization.name} />}
            <div className={cn(photosShown ? 'mt-5 sm:mt-8' : 'mt-4 sm:mt-0')}>
              <BusinessHeader organization={organization} hero={!photosShown} />
            </div>
          </div>
        ) : (
          <h1 className="sr-only">{organization.name}</h1>
        )}

        <div ref={contentRef} className={cn('mt-6 scroll-mt-4', bandShown ? 'sm:mt-10' : 'sm:mt-0')}>
          {children}
        </div>
      </main>

      <footer className="mt-auto flex items-center justify-center gap-1.5 border-t border-line py-7 text-body-sm text-soft">
        {t('footer.poweredBy')} <span className="font-semibold text-ink">Sloty</span>
      </footer>
    </div>
  );
}
